import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import { RelatedDocLink } from "@/modules/docs/RelatedDocLink";
import { DocSlip, printDocSlip } from "@/modules/docs/print/DocSlip";
import { buildSalesDeliveryPrint } from "@/modules/docs/print/doc-print-math";
import { DataTable } from "@/modules/shell/data-table";
import { DocActivity } from "@/modules/shell/DocActivity";
import { EmptyState } from "@/modules/shell/EmptyState";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import { TagsAssigneesPanel } from "@/modules/tags/components/TagsAssigneesPanel";
import {
  getSalesDelivery,
  postSalesDeliveryWriteOff,
} from "@/shared/api/sales";
import { newClientIdempotencyKey } from "@/shared/client-idempotency";
import { notify } from "@/shared/feedback/notify";
import { salesDeliveryLineColumns } from "./so-detail-columns";

export function SalesDeliveryDetailPage() {
  const { companySlug, deliveryId } = useParams();
  const navigate = useNavigate();
  const confirmUi = useConfirm();
  const [data, setData] = useState<Awaited<
    ReturnType<typeof getSalesDelivery>
  > | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function reload() {
    if (!companySlug || !deliveryId) return;
    const d = await getSalesDelivery(companySlug, deliveryId);
    setData(d);
  }

  useEffect(() => {
    let cancelled = false;
    if (!companySlug || !deliveryId) return;
    getSalesDelivery(companySlug, deliveryId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : "Failed";
        if (msg === "Not signed in") navigate("/login", { replace: true });
        else setError(msg);
      });
    return () => {
      cancelled = true;
    };
  }, [companySlug, deliveryId, navigate]);

  async function onWriteOff() {
    if (!companySlug || !deliveryId || !data) return;
    const shorts = data.writeOffLines ?? [];
    if (shorts.length === 0) return;
    const reason = data.delivery.shipmentVarianceReason ?? "variance";
    const summary = shorts
      .map((l) => `${l.qtyShort} × ${l.productName}`)
      .join("\n");
    const ok = await confirmUi({
      title: `Write off as ${reason}?`,
      description: `Removes short qty from ${data.delivery.warehouseName} (still on books until write-off).\n\n${summary}\n\nPosts shrink adjust-out. Audited.`,
      confirmLabel: "Write off",
      destructive: true,
    });
    if (!ok) return;

    setBusy(true);
    setError(null);
    try {
      const result = await postSalesDeliveryWriteOff(companySlug, deliveryId, {
        idempotencyKey: newClientIdempotencyKey("dn-wo"),
      });
      const oversell = result.writeOff.lines.some((l) => l.oversell);
      if (oversell) {
        notify.info("Written off — below recorded on-hand (audited)");
      } else {
        notify.success("Short qty written off");
      }
      await reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Write-off failed");
    } finally {
      setBusy(false);
    }
  }

  if (!companySlug || !deliveryId) return null;
  if (!data) {
    return (
      <PageFrame width="narrow" className="py-4">
        {error ? (
          <Alert variant="destructive">{error}</Alert>
        ) : (
          <Alert variant="muted">Loading delivery…</Alert>
        )}
      </PageFrame>
    );
  }

  const { delivery: del, lines, writeOffLines } = data;
  const canWriteOff =
    Boolean(del.shipmentVarianceReason) &&
    !del.writtenOffAt &&
    (writeOffLines?.length ?? 0) > 0;
  const printData = buildSalesDeliveryPrint({
    number: del.number,
    soNumber: del.soNumber,
    lifecycle: del.lifecycle,
    warehouseName: del.warehouseName,
    warehouseCode: del.warehouseCode,
    issuedAt: del.postedAt,
    lines: lines.map((l) => ({
      productName: l.productName,
      qty: l.qty,
      unitCost: l.unitCost,
    })),
  });

  return (
    <PageFrame width="narrow">
      <div className="pos-receipt-print" aria-hidden>
        <DocSlip data={printData} />
      </div>
      <div className="no-print">
        <PageHeader
          eyebrow="Delivery"
          title={del.number}
          description={[
            del.lifecycle,
            `← ${del.soNumber}`,
            del.warehouseCode || del.warehouseName,
          ]
            .filter(Boolean)
            .join(" · ")}
          actions={
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => printDocSlip()}
            >
              Print
            </Button>
          }
        />
        <TagsAssigneesPanel
          companySlug={companySlug}
          docType="sales_delivery"
          docId={deliveryId}
          linkPath={`/c/${companySlug}/sales/deliveries/${deliveryId}`}
        />
      </div>

      <dl className="mt-6 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-[var(--color-muted)]">Warehouse</dt>
        <dd className="m-0">
          {del.warehouseName}
          {del.warehouseCode ? (
            <span className="font-mono text-xs text-[var(--color-muted)]">
              {" "}
              · {del.warehouseCode}
            </span>
          ) : null}
        </dd>
        {del.fulfillmentMethod ? (
          <>
            <dt className="text-[var(--color-muted)]">Method</dt>
            <dd className="m-0 capitalize">{del.fulfillmentMethod}</dd>
          </>
        ) : null}
        {del.postedAt && (
          <>
            <dt className="text-[var(--color-muted)]">Posted</dt>
            <dd className="m-0 text-xs">{del.postedAt}</dd>
          </>
        )}
        {del.shipmentVarianceReason ? (
          <>
            <dt className="text-[var(--color-muted)]">Variance</dt>
            <dd className="m-0 capitalize">{del.shipmentVarianceReason}</dd>
          </>
        ) : null}
        {del.writtenOffAt ? (
          <>
            <dt className="text-[var(--color-muted)]">Written off</dt>
            <dd className="m-0 text-xs">{del.writtenOffAt}</dd>
          </>
        ) : null}
      </dl>

      <p className="mt-3 text-sm">
        <RelatedDocLink
          companySlug={companySlug}
          kind="sales_order"
          docId={del.salesOrderId}
        >
          {del.soNumber ?? "Sales order"}
        </RelatedDocLink>
        <span className="text-[var(--color-muted)]"> · sales order</span>
      </p>
      {del.salesShipmentId && del.shipmentNumber ? (
        <p className="mt-1 text-sm">
          <Link
            to={`/c/${companySlug}/sales/in-transit/${del.salesShipmentId}`}
            className="font-mono text-sm no-underline hover:underline"
          >
            {del.shipmentNumber}
          </Link>
          <span className="text-[var(--color-muted)]"> · in transit</span>
        </p>
      ) : null}

      {canWriteOff ? (
        <div className="mt-6 rounded-[var(--radius-md)] border border-[var(--color-border)] p-4">
          <h2 className="m-0 text-sm font-semibold">
            Write off short qty ({del.shipmentVarianceReason})
          </h2>
          <p className="mt-1 text-xs text-[var(--color-muted)]">
            Still on warehouse books until written off. Adjust-out hits COGS /
            shrink.
          </p>
          <ul className="mt-2 list-none space-y-1 p-0 text-sm">
            {writeOffLines.map((l) => (
              <li key={l.salesOrderLineId} className="font-mono tabular-nums">
                {l.qtyShort} · {l.productName}
              </li>
            ))}
          </ul>
          <Button
            type="button"
            className="mt-3"
            disabled={busy}
            variant="destructive"
            onClick={() => void onWriteOff()}
          >
            Write off
          </Button>
        </div>
      ) : null}

      <DataTable
        listId="sales-delivery-lines"
        companySlug={companySlug}
        className="mt-6 border-t border-[var(--color-border)]/90 pt-4"
        columns={salesDeliveryLineColumns()}
        data={lines}
        getRowId={(r) => r.id}
        manualSorting={false}
        empty={
          <EmptyState
            title="No lines"
            description="This delivery has no product lines."
          />
        }
      />

      {error && (
        <Alert variant="destructive" className="mt-4">
          {error}
        </Alert>
      )}

      <DocActivity
        companySlug={companySlug}
        docType="sales_delivery"
        docId={deliveryId}
        linkPath={`/c/${companySlug}/sales/deliveries/${deliveryId}`}
      />
    </PageFrame>
  );
}
